import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { structureProperty } from "@/endpoints/dryp/structureProperty";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { Details } from "@/components/IAndI/Details";
import { AppLink } from "@/components/AppLink";
import { formatAdaptive } from "@/utils/number";

interface StructureDetailPanelProps {
	iAndI: Dryp.IAndISummary;
	status: { color: string; label: string; message: string };
	missingUpstreamCount: number;
	flows: { label: string; value?: number; unit?: string }[];
}

const MetricRow = ({ label, value, unit }: { label: string; value?: number; unit?: string }) => (
	<div className="flex items-center justify-between gap-2">
		<dt className="text-muted-foreground">{label}</dt>
		<dd className="font-medium tabular-nums">
			{value != null ? `${formatAdaptive(value)}${unit ? ` ${unit}` : ""}` : "—"}
		</dd>
	</div>
);

export const StructureDetailPanel = observer(
	({ iAndI, status, missingUpstreamCount, flows }: StructureDetailPanelProps) => {
		const propertiesApi = useAsyncStateStore(structureProperty.getForStructure);

		useEffect(() => {
			propertiesApi.fetch(iAndI.structure.id, { pageNumber: 0, pageSize: 100 }, undefined, undefined, {
				requiredForIAndI: true,
			});
		}, [iAndI.structure.id]);

		const properties = propertiesApi.data?.items ?? [];

		return (
			<div className="absolute right-3 top-3 z-10 max-h-[calc(100%-1.5rem)] w-72 overflow-auto rounded-lg border bg-background/95 p-3 shadow backdrop-blur">
				<div className="mb-2 flex items-center justify-between gap-2">
					<AppLink
						to="Dashboard.Structures.Structure"
						params={{ id: iAndI.structure.id }}
						className="text-sm font-semibold leading-tight text-foreground hover:underline"
					>
						{iAndI.structure.name}
					</AppLink>
					<span
						className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium"
						style={{ background: `${status.color}22`, color: status.color }}
					>
						{status.label}
					</span>
				</div>

				<p className="text-[11px] text-muted-foreground">{status.message}</p>
				{missingUpstreamCount > 0 && (
					<p className="mt-1 text-[11px] text-amber-600">
						{missingUpstreamCount} upstream node(s) missing data — incoming is understated.
					</p>
				)}

				<hr className="my-3 border-border" />

				{iAndI.parts && <Details breakdown={iAndI.parts} className="text-[11px]" />}

				<hr className="my-3 border-border" />

				<div className="text-xs font-semibold">Info</div>
				<dl className="mt-1 flex flex-col gap-1 text-[11px]">
					<MetricRow
						label="Infiltration volume"
						value={iAndI.parts?.infiltration.volume?.value}
						unit={iAndI.parts?.infiltration.volume?.unit.symbol}
					/>
					<MetricRow
						label="Inflow volume"
						value={iAndI.parts?.inflow.volume?.value}
						unit={iAndI.parts?.inflow.volume?.unit?.symbol}
					/>
					<MetricRow
						label="Infiltration / pipe"
						value={iAndI.parts?.infiltration.flowPerLength?.value}
						unit={iAndI.parts?.infiltration.flowPerLength?.unit?.symbol}
					/>
					<MetricRow
						label="Inflow / pipe"
						value={iAndI?.parts?.inflow?.flowPerLength?.value}
						unit={iAndI?.parts?.inflow?.flowPerLength?.unit?.symbol}
					/>
					<div className="flex items-center justify-between gap-2">
						<dt className="text-muted-foreground">Network type</dt>
						<dd className="font-medium capitalize">{iAndI.structure.networkType}</dd>
					</div>
				</dl>

				<hr className="my-3 border-border" />

				<div className="text-xs font-semibold">Flow</div>
				<dl className="mt-1 flex flex-col gap-1 text-[11px]">
					{flows.map(flow => (
						<MetricRow key={flow.label} label={flow.label} value={flow.value} unit={flow.unit} />
					))}
				</dl>

				<hr className="my-3 border-border" />

				<div className="text-xs font-semibold">I&I properties</div>
				{propertiesApi.loading ? (
					<p className="mt-1 text-[11px] text-muted-foreground">Loading…</p>
				) : properties.length === 0 ? (
					<p className="mt-1 text-[11px] text-muted-foreground">No properties found.</p>
				) : (
					<div className="mt-1 flex flex-col text-[11px]">
						{properties.map(property => {
							const hasValue = property.value != null;
							return (
								<div
									key={property.typeId}
									className="flex items-start justify-between gap-2 border-b py-1 last:border-b-0"
								>
									<span className="min-w-0 break-words text-muted-foreground">
										{property.typeName}
									</span>
									<span className="shrink-0 text-right tabular-nums">
										{hasValue ? (
											<>
												<span className="font-medium text-foreground">{property.value}</span>
												{property.unit && (
													<span className="ml-0.5 text-muted-foreground">
														{property.unit}
													</span>
												)}
											</>
										) : (
											<span className="text-muted-foreground">—</span>
										)}
									</span>
								</div>
							);
						})}
					</div>
				)}
			</div>
		);
	},
);
